import { inject, Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { EMPTY, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import {
    LicenseType,
    MAX_ALLOWED_TRIAL_INVITATIONS,
    PublicPaymentInfo,
    PublicPaymentInfoDao,
} from '../models/public-payment-info';
import { PaymentRepository } from '../repositories/payment.repository';
import { TenantAccessService } from './tenant-access.service';

@Injectable({
    providedIn: 'root',
})
export class PaymentService {
    private readonly paymentRepository: PaymentRepository = inject(PaymentRepository);
    private readonly tenantAccessService = inject(TenantAccessService);

    private readonly paymentInformation$: Observable<PublicPaymentInfo> = this.tenantAccessService.tenantUid$.pipe(
        switchMap((uid) => (uid ? this.readPaymentInformation() : EMPTY)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public getTenantPublicPaymentInformation(): Observable<PublicPaymentInfo> {
        return this.paymentInformation$;
    }

    public getLicenseType(): Observable<LicenseType> {
        return this.isTestPhase().pipe(
            switchMap((isTestPhase) => {
                if (isTestPhase) {
                    return of(LicenseType.ENTERPRISE);
                } else {
                    return this.paymentInformation$.pipe(map((info) => info.license));
                }
            }),
        );
    }

    public isFreeLicense(): Observable<boolean> {
        return this.getLicenseType().pipe(map((license) => license === LicenseType.FREE));
    }

    public isTestPhase(): Observable<boolean> {
        return this.paymentInformation$.pipe(
            map((info) => info.license === LicenseType.FREE && new Date(info.endOfFreeTrial) > new Date()),
        );
    }

    private readPaymentInformation(): Observable<PublicPaymentInfo> {
        return this.paymentRepository.getPublicPaymentInformationForTenant().pipe(
            map((doc) => {
                return this.mapDaoToModel(doc);
            }),
        );
    }

    private mapDaoToModel(doc: PublicPaymentInfoDao | undefined): PublicPaymentInfo {
        const license = doc?.license ?? LicenseType.FREE;
        const endOfFreeTrial = doc?.endOfFreeTrial ?? DateTime.now().plus({ days: -10 }).toISO();
        const invitationsLeft = Math.max(0, MAX_ALLOWED_TRIAL_INVITATIONS - (doc?.trialInvitationsCount ?? 0));
        const isTestPhase = license === LicenseType.FREE && new Date(endOfFreeTrial) > new Date();
        return {
            owner: doc?.owner ?? '',
            license,
            endOfFreeTrial,
            invitationsLeft,
            invitationLimitReached: isTestPhase && invitationsLeft < 1,
        };
    }
}
